import { FinanceType } from '@upstart/harmony/lib/constants';
import React, { FunctionComponent, useState } from 'react';
import { MdEdit, MdCheck, MdWarning } from 'react-icons/md';
import { InfoOutlined } from '@mui/icons-material';
import moment from 'moment';
import classNames from 'classnames';
import { Loader } from 'semantic-ui-react';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { startCase } from 'lodash';
import { Tooltip } from '@mui/material';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import { isMarketScanIncentivesEnabled } from '@upstart/harmony/lib/dealer-utils';
import Input from '../Input';

function isValidNumber(input?: number | string) {
  if (!input) {
    return false;
  }
  if (typeof input === 'number') {
    return true;
  }
  const parsedValue = parseInt(input, 10);
  return !isNaN(parsedValue) && parsedValue > 0;
}

interface Props {
  offer: DeskingOffer;
  financeType?: FinanceType;
  months?: number;
  mileage?: number;
  onChange?: (input: {
    months?: number;
    mileage?: number;
    isMSIncentivesEnabled?: boolean;
  }) => void;
  displayingAllSelectedOffers?: boolean;
  errors?: Record<string, string>;
  readOnlyMode: boolean;
  isReadOnlyLoading: boolean;
  isViewingCurrentPencilVersion: boolean;
  dealer: DealerWithFlags;
}

const FinanceTypeRow: FunctionComponent<Props> = props => {
  const {
    offer,
    financeType,
    months,
    mileage,
    onChange,
    displayingAllSelectedOffers,
    errors,
    readOnlyMode,
    isReadOnlyLoading,
    isViewingCurrentPencilVersion,
    dealer,
  } = props;
  // Enable edit mode if there are any initial errors on the offer
  const hasError = !!(errors?.months || errors?.mileage);
  const [isEditMode, setEditMode] = useState(hasError || false);
  const [currentInput, setCurrentInput] = useState<{
    months?: number | string;
    mileage?: number | string;
  }>({ months, mileage });

  if (
    financeType === FinanceType.CASH ||
    (typeof months === 'undefined' && typeof mileage === 'undefined')
  ) {
    return (
      <div className="finance-type-row">
        <div className="title">{financeType}</div>
      </div>
    );
  }

  // Valid input is when the initial value (months/mileage) is provided and the
  //  current input is a valid number
  const isValidInput =
    (typeof months === 'undefined' || isValidNumber(currentInput.months)) &&
    (typeof mileage === 'undefined' || isValidNumber(currentInput.mileage));

  const onSubmit = () => {
    setEditMode(!isEditMode);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isValidInput) {
      setEditMode(false);
    }
  };

  const onChangeMonth = (newValue: string | boolean) => {
    const newMonths = typeof newValue === 'boolean' ? '0' : newValue;
    setCurrentInput({
      ...currentInput,
      months: newMonths,
    });
    if (onChange && isValidNumber(newMonths)) {
      onChange({
        months:
          typeof newMonths === 'string' ? parseFloat(newMonths) : newMonths,
        mileage:
          typeof currentInput.mileage === 'string'
            ? parseFloat(currentInput.mileage)
            : currentInput.mileage,
        isMSIncentivesEnabled: isMarketScanIncentivesEnabled(dealer),
      });
    }
  };

  const onChangeMileage = (newValue: string | boolean) => {
    const newMileage = typeof newValue === 'boolean' ? '0' : newValue;
    setCurrentInput({
      ...currentInput,
      mileage: newMileage,
    });
    if (onChange && isValidNumber(newMileage)) {
      onChange({
        months:
          typeof currentInput.months === 'string'
            ? parseFloat(currentInput.months)
            : currentInput.months,
        mileage:
          typeof newMileage === 'string' ? parseFloat(newMileage) : newMileage,
      });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isEditMode && e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement)?.blur();
      onSubmit();
    }
  };

  const showUnifiedLeaseValues =
    financeType === FinanceType.LEASE && displayingAllSelectedOffers;
  const showOfferCategory = displayingAllSelectedOffers;

  const financeTypeRow = (
    <div className="finance-type-row">
      <div>
        <span className="title">{`${financeType} - `}</span>
        {/* eslint-disable-next-line no-nested-ternary */}
        {financeType === FinanceType.PURCHASE || showUnifiedLeaseValues ? (
          <span
            className={classNames('input-container', {
              error: errors?.months || !isValidNumber(currentInput.months),
            })}
          >
            {isEditMode ? (
              <Input
                className={classNames('form-control months')}
                type="number"
                value={currentInput.months}
                onChange={onChangeMonth}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
              />
            ) : (
              <span>{months}</span>
            )}
            <span className="units">
              {showUnifiedLeaseValues ? ' months | ' : ' months'}
            </span>
          </span>
        ) : null}
        {financeType === FinanceType.PURCHASE &&
          showOfferCategory &&
          offer?.data?.category?.type && (
            <span> | {startCase(offer?.data?.category?.type)}</span>
          )}
        {/* eslint-disable-next-line no-nested-ternary */}
        {financeType === FinanceType.LEASE ? (
          <span
            className={classNames('input-container', {
              error: errors?.mileage || !isValidNumber(currentInput.mileage),
            })}
          >
            {isEditMode ? (
              <Input
                className={classNames('form-control mileage')}
                type="number"
                value={currentInput.mileage}
                onChange={onChangeMileage}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
              />
            ) : (
              <span>{mileage}</span>
            )}
            <span className="units">{' mi'}</span>
          </span>
        ) : null}
      </div>
      {!readOnlyMode && (
        <button
          type="button"
          className="edit-button"
          disabled={!isValidInput}
          onClick={onSubmit}
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {!isValidInput ? (
            <MdWarning className="icon error" />
          ) : isEditMode ? (
            <MdCheck className="icon" />
          ) : (
            <MdEdit className="icon" />
          )}
        </button>
      )}
    </div>
  );

  const iconElement =
    isReadOnlyLoading || !offer.createdAt || isViewingCurrentPencilVersion ? (
      <span
        data-tooltip="The desking offer being updated.."
        data-position="bottom center"
      >
        <Loader size="small" active={isReadOnlyLoading} inline />
      </span>
    ) : (
      <Tooltip
        title={
          <span style={{ fontSize: '12px' }}>
            Version from{' '}
            {moment(offer.createdAt).format('MMMM DD, YYYY h:mm A')}
          </span>
        }
        placement="top-end"
        arrow
      >
        <InfoOutlined fontSize="large" sx={{ color: '#177dfb' }} />
      </Tooltip>
    );

  return readOnlyMode ? (
    <div className="finance-type-row-with-loader">
      {financeTypeRow}
      {iconElement}
    </div>
  ) : (
    financeTypeRow
  );
};

export default FinanceTypeRow;
